import { raiseGear } from "@highmarch/domain";
import type { GearItem, GearOwner } from "@highmarch/domain";
import { equipOn, raiseItem, raiseItemMax } from "@highmarch/app";
import { audio } from "../../audio/audio";
import { ToastHandle, toast } from "../toast";
import type { ArmouryCtx } from "./host";
import { fullTitle, refusal } from "./itemBits";
import { celebrateMythic } from "./itemFx";
import { guide } from "../guide/guide";

/** The one equip / upgrade path every item surface takes, with its feedback, and the Undo toast a salvage offers. */

let undo: ToastHandle | null = null;
/** An Undo toast; any later spend (an upgrade) or a new Undo closes the old one, since its restore would be refused. */
export function offerUndo(ctx: ArmouryCtx, text: string, restore: () => void): void {
  undo?.dismiss();
  undo = toast(ctx.toastParent, text, { action: { label: "Undo", onTap: () => { undo = null; restore(); } } });
}
export function closeUndo(): void { undo?.dismiss(); undo = null; }

/**
 * Equips from any item surface: `equipOn` (takes it off wherever it is, the old item goes to the stash), then
 * `ctx.changed()`, a UI blip, a toast when it bound, and the Mythic banner when its effect acts on this owner.
 * Also tells the shell (an `armoury:equipped` event on the sheet) so its socket can pop. False when refused.
 */
export function equipItem(ctx: ArmouryCtx, it: GearItem, owner: GearOwner, mythicActive: boolean): boolean {
  const wasBound = it.bound;
  const r = equipOn(ctx.profile, it.id, owner);
  if (r !== true) { toast(ctx.toastParent, refusal(r)); return false; }
  audio.play("ui");
  guide()?.signal("gearEquipped");
  ctx.changed();
  if (!wasBound && it.bound) toast(ctx.toastParent, `${fullTitle(ctx.B, it)} is now bound: it can't be traded later`);
  if (mythicActive) celebrateMythic(ctx, it, owner);
  if (typeof CustomEvent === "function") ctx.sheet.dispatchEvent(new CustomEvent("armoury:equipped", { detail: { owner, slot: it.slot, itemId: it.id } }));
  return true;
}

/** One level up for Forge Essence (gear v2, the domain's raiseGear); closes a pending Undo. True, or the refusal. */
export function upgradeItem(ctx: ArmouryCtx, it: GearItem): true | string {
  const r = raiseGear(ctx.profile, it.id);
  if (r !== true) return r;
  closeUndo();
  audio.play("towerUpgrade");
  guide()?.signal("gearRaised");
  ctx.changed();
  return true;
}


/**
 * Raise to a level (gear v2's Raise max, `raiseItemMax`) or one level (`raiseItem`): the app's use case, then the same
 * feedback as `upgradeItem` (the bigger chime for several levels). The levels gained and the essence spent, or the refusal.
 */
export function raiseItemOn(ctx: ArmouryCtx, it: GearItem, max: boolean): { readonly levels: number; readonly essence: number } | string {
  const r = max ? raiseItemMax(ctx.profile, it.id) : raiseItem(ctx.profile, it.id);
  if (typeof r === "string") return r;
  if (r.levels <= 0) return "not enough essence";
  closeUndo();
  audio.play(r.levels > 1 ? "towerUpgradeMax" : "towerUpgrade");
  guide()?.signal("gearRaised");
  ctx.changed();
  return r;
}
